import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { Box, Button, FormControl, Link, TextField, Typography, Select, MenuItem, InputAdornment, IconButton, } from "@mui/material";

import ForgotPassword from "../forgetPassword/ForgotPassword";
import CustomCard from "../../components/cards/CustomCard";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { validateLoginFields } from "../../utils/validation/validation";
import { loginRequest } from "../../services/api/auth";
import { SUCCESS_MESSAGES } from "../../constant/messages";
import { showToast } from "../../store/slices/toastSlice";
import { login } from "../../store/slices/authSlice";

import { createNewAccountLink, facebookDropdownStyles, facebookTextFieldStyles, forgetPasswordLink, loginButton } from "../../styles/textFieldStyles";
import { ROUTE_PATHS } from "../../routes/routePaths";

export const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState({ email: "", password: "", });
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const registerSuccess = localStorage.getItem("registerSuccess");
    const logoutSuccess = localStorage.getItem("logoutSuccess");

    if (registerSuccess) {
      dispatch(
        showToast({
          message: SUCCESS_MESSAGES.USER_REGISTERED_SUCCESSFULLY,
          severity: "success",
        })
      );
      localStorage.removeItem("registerSuccess");
    }

    if (logoutSuccess) {
      dispatch(showToast({ message: logoutSuccess, severity: "success" }));
      localStorage.removeItem("logoutSuccess");
    }
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    const validationResult = validateLoginFields(credentials);
    if (!validationResult.valid) {
      dispatch(
        showToast({ message: validationResult.message, severity: "error" })
      );
      return;
    }

    try {
      const data = await loginRequest(credentials);

      if (data.responseCode === 200) {
        localStorage.setItem("token", data.token);
        dispatch(login(data.token));
        dispatch(showToast({ message: data.message, severity: "success" }));
        navigate(ROUTE_PATHS.DASHBOARD.LIST);
      }
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  return (
    <CustomCard topMargin={100}>
      <Typography
        variant="h6"
        sx={{ width: "100%", fontWeight: 600, mb: 2 }}
      >
        Log in to Rently
      </Typography>
      <Box
        component="form"
        onSubmit={handleLogin}
        noValidate
        sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2 }}
      >
        <FormControl>
          <TextField
            id="email"
            name="email"
            label="Email address or mobile number"
            required
            fullWidth
            value={credentials.email}
            onChange={handleChange}
            InputProps={{ notched: false }}
            sx={facebookTextFieldStyles}
          />
        </FormControl>

        <FormControl>
          <TextField
            id="password"
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"} // Switches dynamically between text and password masks
            required
            fullWidth
            value={credentials.password}
            onChange={handleChange}
            InputProps={{
              notched: false,
              endAdornment: credentials.password ? (
                <InputAdornment position="end" sx={{ mt: "12px", mr: "4px" }}>
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                    style={{ color: "#606770" }}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={facebookTextFieldStyles}
          />
        </FormControl>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={loginButton}
        >
          Log in
        </Button>

        <Box sx={{ display: "flex", width: '100%', justifyContent: "center", mb: 0.5 }}>
          <Link
            component="button"
            type="button"
            onClick={() => setForgotPasswordOpen(true)}
            sx={forgetPasswordLink}
          >
            Forgotten password?
          </Link>
        </Box>

        <Box sx={{ display: "flex", width: '100%', justifyContent: "center", mt: 5, mb: 5 }}>
          <Link
            component="button"
            type="button"
            onClick={() => navigate(ROUTE_PATHS.PUBLIC_ROUTES.REGISTER)}
            sx={createNewAccountLink}
          >
            Create new account
          </Link>
        </Box>
      </Box>

      <ForgotPassword
        open={forgotPasswordOpen}
        handleClose={() => setForgotPasswordOpen(false)}
      />

    </CustomCard>
  );
};
